<template>
  <div class="income-tax-calculator">
    <NavBar sticky>
      <template #left>
        <ButtonIconContextual
          label="Voltar para página anterior"
          size="small"
          icon="EF0030"
          @click="handleCloseButton"
        />
      </template>
    </NavBar>
    <main>
      <FadeTransition>
        <div
          v-if="currentViewStatus === ViewStatus.Error"
          class="income-tax-calculator__helper-view"
        >
          <HelperViewStatus
            class="income-tax-calculator__helper-view__card"
            :title="data.error.title"
            :description="data.error.description"
            :button-label="data.error.buttonLabel"
            status="negative"
            @click="fetchViewData"
          />
        </div>
        <TaxCalculatorLoader
          v-else-if="currentViewStatus === ViewStatus.Loading"
        />
        <TaxCalculatorContent v-else />
      </FadeTransition>
      <FadeTransition>
        <ButtonContainer
          v-if="!(currentViewStatus === ViewStatus.Error)"
          class="income-tax-calculator__button-container"
        >
          <template #button-primary>
            <ButtonPrimary
              size="large"
              :label="buttonLabel"
              :disabled="currentViewStatus === ViewStatus.Loading"
              :loading="
                currentViewStatus === ViewStatus.Loading || isButtonLoading
              "
              @click="handleButtonAction"
            />
          </template>
        </ButtonContainer>
      </FadeTransition>
    </main>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';

import {
  ButtonContainer,
  ButtonPrimary,
  ButtonIconContextual,
  HelperViewStatus,
  NavBar
} from '@warrenbrasil/nebraska-web';
import { TaxCalculatorContent } from './components/TaxCalculatorContent';
import { TaxCalculatorLoader } from './components/TaxCalculatorLoader';
import { FadeTransition } from '@/modules/common/components/FadeTransition';

import {
  checkIfHasAccountOnIncomeTaxCalculatorProvider,
  generateAccessLinkForIncomeTaxCalculatorProvider
} from '@/modules/account/services/account';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import { ViewStatus } from './types';
import { data } from './data';

@Component({
  components: {
    ButtonContainer,
    ButtonPrimary,
    ButtonIconContextual,
    FadeTransition,
    HelperViewStatus,
    NavBar,
    TaxCalculatorContent,
    TaxCalculatorLoader
  }
})
export default class IncomeTaxCalculator extends Vue {
  readonly ViewStatus = ViewStatus;
  readonly data = data;

  isButtonLoading = false;
  currentViewStatus = ViewStatus.Loading;
  hasAccountOnIncomeTaxCalculatorProvider = false;

  created() {
    this.fetchViewData();
  }

  get shouldGoBack() {
    return !!this.$route.query.goBack;
  }

  get buttonLabel() {
    return this.hasAccountOnIncomeTaxCalculatorProvider
      ? 'Acessar VeloPrime'
      : 'Aprovar integração com a VeloPrime';
  }

  handleCloseButton() {
    this.shouldGoBack
      ? this.$router.back()
      : this.$router.replace({ name: 'home' });
  }

  async handleButtonAction() {
    try {
      this.isButtonLoading = true;

      const { accessLink } =
        await generateAccessLinkForIncomeTaxCalculatorProvider();

      window.open(accessLink, '_blank');
    } catch {
      const errorMsg = this.hasAccountOnIncomeTaxCalculatorProvider
        ? 'Ocorreu algum problema ao tentar acessar a sua conta na VeloPrime, tente novamente mais tarde.'
        : 'Ocorreu algum problema ao criar sua conta na VeloPrime, tente novamente mais tarde.';

      this.$notify({
        group: 'wrn-notifications',
        text: errorMsg,
        type: NotificationTypes.error
      });
    } finally {
      this.isButtonLoading = false;
    }
  }

  async fetchViewData() {
    try {
      this.currentViewStatus = ViewStatus.Loading;

      const { hasAccountOnProvider } =
        await checkIfHasAccountOnIncomeTaxCalculatorProvider();

      this.hasAccountOnIncomeTaxCalculatorProvider = hasAccountOnProvider;

      this.currentViewStatus = ViewStatus.Default;
    } catch {
      this.currentViewStatus = ViewStatus.Error;
    }
  }
}
</script>

<style lang="less" scoped>
@navbar-height: 80px;
@footer-height: 80px;

.income-tax-calculator {
  position: relative;
  padding-bottom: @size-spacing-x1200;

  &__helper-view {
    min-height: calc(100vh - @navbar-height - @footer-height);
    display: flex;
    justify-content: center;
    align-items: center;

    &__card {
      margin: @size-spacing-x600 0;
    }
  }

  &__button-container {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background: @background-secondary;
    z-index: 10;
  }
}
</style>
